import React, { useState, useEffect, useCallback } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import SearchBar from '../../TrendRadarChart/components/SearchBar';
import useSearch from '../../TrendRadarChart/hooks/useSearch';
import { useRadarData } from '../../../hooks/useRadarData';

/**
 * Reusable technology filter component with multi-select support.
 * Uses the radar SearchBar to search technologies and displays
 * selected items as removable chips.
 *
 * @param {Object} props
 * @param {Array<{id: string, name: string}>} props.value - Currently selected technologies
 * @param {Function} props.onChange - Callback when selections change: (newSelections) => void
 * @param {string} [props.placeholder] - Placeholder text for the search input
 * @param {number} [props.searchWidth] - Width of the search bar in px (default 450)
 * @param {boolean} [props.singleSelect] - If true, only one technology can be selected at a time
 * @param {Array<string>} [props.excludeIds] - Leaf segment IDs to exclude from search results
 * @param {object} [props.sx] - Additional sx styles for the outer container
 */
const TechnologyFilter = ({
  value = [],
  onChange,
  placeholder,
  searchWidth = 450,
  singleSelect = false,
  excludeIds = [],
  sx = {},
}) => {
  const { data: radarData } = useRadarData();
  const {
    searchResults,
    showResults,
    isGlobalSearch,
    handleSearch,
    setIsGlobalSearch,
  } = useSearch(radarData || {}, null);
  const [searchTerm, setSearchTerm] = useState('');

  const excludeSet = new Set(excludeIds);
  const filteredSearchResults = excludeSet.size > 0
    ? searchResults.filter((r) => !excludeSet.has(r.node?.leafSegmentId))
    : searchResults;

  useEffect(() => {
    setIsGlobalSearch(true);
  }, [setIsGlobalSearch]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    handleSearch(e);
  }, [handleSearch]);

  const handleClear = useCallback(() => {
    setSearchTerm('');
    handleSearch({ target: { value: '' } });
  }, [handleSearch]);

  const handleResultSelect = useCallback((result) => {
    const newItem = { id: result.leafSegmentId, name: result.name };

    if (singleSelect) {
      onChange([newItem]);
    } else {
      // Avoid duplicates
      if (!value.some((t) => t.id === result.leafSegmentId)) {
        onChange([...value, newItem]);
      }
    }

    setSearchTerm('');
    handleSearch({ target: { value: '' } });
  }, [value, onChange, singleSelect, handleSearch]);

  const handleRemove = useCallback((id) => {
    onChange(value.filter((t) => t.id !== id));
  }, [value, onChange]);

  const handleClearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', ...sx }}>
      <Box sx={{ minWidth: searchWidth, flex: `0 0 ${searchWidth}px`, position: 'relative', zIndex: 10 }}>
        <SearchBar
          searchTerm={searchTerm}
          searchResults={filteredSearchResults}
          showResults={showResults}
          isGlobalSearch={isGlobalSearch}
          selectedSubcategory={null}
          onSearch={handleSearchChange}
          onResultSelect={handleResultSelect}
          onToggleSearchMode={setIsGlobalSearch}
          showNoResultsMessage={false}
          width="100%"
          onClear={handleClear}
        />
      </Box>
      {value.map((tech) => (
        <Chip
          key={tech.id}
          label={tech.name}
          onDelete={() => handleRemove(tech.id)}
          size="small"
          sx={{ height: 28, fontSize: '12px' }}
        />
      ))}
      {value.length > 1 && (
        <Typography
          variant="caption"
          sx={{ color: '#64748B', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={handleClearAll}
        >
          Clear all
        </Typography>
      )}
    </Box>
  );
};

export default TechnologyFilter;
